import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import orderBy from 'lodash/orderBy';

import { useTranslation, Trans } from 'react-i18next';

// ui
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';

// font and icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';

// components
import { StyledIconButton } from '../../../../components/button/Button';
import TableHeaderEnhance from '../../../../components/table/TableHeaderEnhance';
import areas from '../../../../assets/json/region';
import DamMediumColor from '../../../../components/color/dam/DamMediumColor';
import DamChartDailyMediumMain from '../../chart/daily/DamChartDailyMediumMain';
import DamChartDailyByRegionMain from '../../chart/daily/DamChartDailyByRegionMain';

// utils
import formatNumber from '../../../../utils/number/Number';

// styles
import styles from '../style';
import {
  StyledFooterSubTableCell,
  StyledFooterTableCell,
  StyledSubTableCell,
} from '../../../../components/table/StyleTable';
import '../../styles.css';

const columns = [
  {
    id: 'dam.dam_name.th',
    disablePadding: false,
    label: <Trans>อ่างเก็บน้ำ</Trans>,
    sortable: true,
  },
  {
    id: 'geocode.province_name.th',
    numeric: false,
    disablePadding: true,
    label: <Trans>จังหวัด</Trans>,
    sortable: true,
  },
  {
    id: 'basin.basin_name.th',
    label: <Trans>ลุ่มน้ำ</Trans>,
    sortable: true,
  },
  {
    id: 'dam.normal_storage',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ความจุ</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam.min_storage',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>กักเก็บต่ำสุด</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam_storage',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ปริมาณน้ำ</Trans>,
    unit: <Trans>ล้าน ลบ.ม. (% ความจุ)</Trans>,
    sortable: true,
  },
  {
    id: 'dam_uses_water',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>ปริมาตรใช้การ</Trans>,
    unit: <Trans>ล้าน ลบ.ม. (%น้ำใช้การ)</Trans>,
    sortable: true,
  },
  {
    id: 'dam_inflow',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>น้ำไหลลง</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'dam_released',
    align: 'right',
    numeric: true,
    disablePadding: false,
    label: <Trans>น้ำระบาย</Trans>,
    unit: <Trans>ล้าน ลบ.ม.</Trans>,
    sortable: true,
  },
  {
    id: 'chart',
    numeric: false,
    disablePadding: false,
    label: '',
  },
];

const useStyles = makeStyles(styles);

export default function DamMediumDataLists(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const { datas, handleModalChanage } = props;
  const filterData = datas.filter((d) => d.agency.id === 12 && d.id !== 0);

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // table sorting
  const [state, setState] = useState({
    order: 'desc',
    orderBy: 'dam_storage_percent',
  });

  // set total summary
  let grandTotalNormalStorage = 0;
  let grandTotalMinStorage = 0;
  let grandTotalInflow = 0;
  let grandTotalReleased = 0;
  let grandTotalDamStorage = 0;
  let grandTotalDamUseWater = 0;

  const handleChart = (data) => (e) => {
    e.preventDefault();

    handleModalChanage({
      open: true,
      header: t('กราฟอ่างเก็บน้ำขนาดกลางรายวัน กรมชลประทาน'),
      subheader: null,
      content: <DamChartDailyMediumMain id={data} agencyId={12} />,
    });
  };

  const handleChartSumByRegion = (regionId) => (e) => {
    e.preventDefault();

    handleModalChanage({
      open: true,
      header: t('กราฟอ่างเก็บน้ำรายวัน รายภาค กรมชลประทาน'),
      subheader: null,
      content: <DamChartDailyByRegionMain id={regionId} />,
    });
  };

  const handleRequestSort = (event, property) => {
    const orderByProperty = property;
    let orderLocal = 'desc';

    if (state.orderBy === property && state.order === 'desc') {
      orderLocal = 'asc';
    }

    setState({ order: orderLocal, orderBy: orderByProperty });
  };

  return (
    <>
      <Paper className={classes.root}>
        <TableContainer style={{ maxHeight: 650 }}>
          <Table stickyHeader aria-label="sticky table" id="medium_dam">
            {/* table header */}
            <TableHeaderEnhance
              firstColumnSticky
              columnData={columns}
              order={state.order}
              orderBy={state.orderBy}
              onRequestSort={handleRequestSort}
            />
            {/* END table header */}

            <TableBody>
              {filterData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10}>
                    {isLoading ? ( // Check if data is loading
                      <strong>{t('กำลังโหลดข้อมูล...')}</strong>
                    ) : (
                      <strong>{t('ไม่มีข้อมูลแหล่งน้ำขนาดกลาง ตามเงื่อนไข')}</strong>
                    )}
                  </TableCell>
                </TableRow>
              )}

              {/* area group title */}
              {areas
                .map((area) => {
                  // summary value by region
                  let totalNormalStorage = 0;
                  let totalMinStorage = 0;
                  let totalInflow = 0;
                  let totalReleased = 0;
                  let totalDamStorage = 0;
                  let totalDamUseWater = 0;

                  // count total records for each area
                  let totalDamInArea = 0;
                  if (filterData.length > 0) {
                    totalDamInArea = filterData
                      .filter((row) => row.geocode.area_code === area.id.toString())
                      .length;
                  }
                  if (totalDamInArea === 0) return null;

                  return (
                    <Fragment key={area.id}>
                      <TableRow>
                        <StyledSubTableCell bgcolor="primary" className="first-column-sticky">
                          {t(area.name)}
                          {' '}
                          {`(${totalDamInArea})`}
                        </StyledSubTableCell>
                        <StyledSubTableCell colSpan={13} bgcolor="primary" />
                      </TableRow>

                      {/* data rows */}
                      {orderBy(filterData, state.orderBy, state.order)
                        .filter((row) => row.geocode.area_code === area.id.toString())
                        .map((row) => {
                          const status = DamMediumColor(row.dam_storage_percent);

                          // calculate percent dam_uses_water
                          let percentDamUsesWater = '--';
                          if (row.dam.min_storage && row.dam.normal_storage && row.dam_uses_water) {
                            percentDamUsesWater = (
                              (row.dam_uses_water * 100) / (row.dam.normal_storage - row.dam.min_storage)
                            ).toFixed(0);
                          }

                          totalNormalStorage += row.dam.normal_storage;
                          if (row.dam.min_storage) {
                            totalMinStorage += row.dam.min_storage;
                          }
                          totalInflow += row.dam_inflow;
                          totalReleased += row.dam_released;
                          totalDamStorage += row.dam_storage;
                          totalDamUseWater += row.dam_uses_water;

                          // grand total
                          grandTotalNormalStorage += row.dam.normal_storage;
                          if (row.dam.min_storage) {
                            grandTotalMinStorage += row.dam.min_storage;
                          }
                          grandTotalInflow += row.dam_inflow;
                          grandTotalReleased += row.dam_released;
                          grandTotalDamStorage += row.dam_storage;
                          grandTotalDamUseWater += row.dam_uses_water;

                          return (
                            <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                              <TableCell className="first-column-sticky">
                                {row.dam.dam_name.th}
                              </TableCell>
                              <TableCell>
                                {row.geocode.province_name.th}
                              </TableCell>
                              <TableCell>
                                {row.basin.basin_name.th.replace('ลุ่มน้ำ', '')}
                              </TableCell>
                              <TableCell align="right">
                                {row.dam.normal_storage}
                              </TableCell>
                              <TableCell align="right">
                                {row.dam.min_storage}
                              </TableCell>
                              <TableCell align="right">
                                <span>
                                  {row.dam_storage}
                                  {' '}
                                  {row.dam_storage_percent
                                  && (
                                    <span style={{ color: status.color.statusColor }}>
                                      (
                                      {row.dam_storage_percent.toFixed(0)}
                                      %)
                                    </span>
                                  )}
                                </span>
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_uses_water}
                                {' '}
                                {percentDamUsesWater !== '--'
                                && (
                                  <>
                                    (
                                    {percentDamUsesWater}
                                    %)
                                  </>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_inflow}
                              </TableCell>
                              <TableCell align="right">
                                {row.dam_released}
                              </TableCell>
                              <TableCell align="right">
                                <Tooltip title={t('กราฟรายปี')}>
                                  <StyledIconButton onClick={handleChart(row.dam.id)}>
                                    <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                                  </StyledIconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      {/* END data rows */}

                      {/* sum by each region */}
                      <TableRow>
                        <StyledFooterTableCell className="first-column-sticky">
                          <Box fontWeight="bold">
                            {t(`รวม${area.name}`)}
                          </Box>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell />
                        <StyledFooterTableCell />
                        <StyledFooterTableCell align="right">
                          <strong>
                            {formatNumber(totalNormalStorage.toFixed(0))}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {formatNumber(totalMinStorage.toFixed(0))}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {formatNumber(totalDamStorage.toFixed(0))}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {formatNumber(totalDamUseWater.toFixed(0))}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {totalInflow.toFixed(2)}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <strong>
                            {totalReleased.toFixed(2)}
                          </strong>
                        </StyledFooterTableCell>
                        <StyledFooterTableCell align="right">
                          <Tooltip title={t('กราฟรายปี')}>
                            <StyledIconButton onClick={handleChartSumByRegion(area.id)}>
                              <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                            </StyledIconButton>
                          </Tooltip>
                        </StyledFooterTableCell>
                      </TableRow>
                      {/* END sum by each region */}
                    </Fragment>
                  );
                })}
              {/* END area group title */}

              {/* grand total all regions */}
              {filterData.length !== 0 && (
                <TableRow>
                  <StyledFooterSubTableCell className="first-column-sticky">
                    <Box fontWeight="bold">{t('รวมทั้งประเทศ')}</Box>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell />
                  <StyledFooterSubTableCell />
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalNormalStorage.toFixed(0))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalMinStorage.toFixed(0))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalDamStorage.toFixed(0))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalDamUseWater.toFixed(0))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalInflow.toFixed(0))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <strong>
                      {formatNumber(grandTotalReleased.toFixed(0))}
                    </strong>
                  </StyledFooterSubTableCell>
                  <StyledFooterSubTableCell align="right">
                    <Tooltip title={t('กราฟรายปี')}>
                      <StyledIconButton onClick={handleChartSumByRegion(0)}>
                        <FontAwesomeIcon icon={faChartLine} style={{ color: '#888' }} />
                      </StyledIconButton>
                    </Tooltip>
                  </StyledFooterSubTableCell>
                </TableRow>
              )}
              {/* END grand total all regions */}

            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}

DamMediumDataLists.propTypes = {
  datas: PropTypes.instanceOf(Array).isRequired,
  handleModalChanage: PropTypes.func.isRequired,
};
